import { useCallback, useMemo } from 'react';

// @ts-expect-error - @return-rabbit/nexus module declaration is not yet available
import { Init, Details } from '@return-rabbit/nexus';

import get from 'lodash.get';

import { CreateEnhancedReturnDrawer } from '../return/create-enhanced-return-drawer/create-enhanced-return-drawer';
import { ReturnConfigProvider } from './return-config-provider';
import { EnhancedReturnAdapterForFulfillmentPlannerContext } from '../../../fulfillment-planner/adapters/enhanced-return';
import { FulfillmentPlanRestrictionsContextProvider } from '../fulfillment-plan/restrictions-context';
import { ConfirmReturn } from './steps/confirm-return';
import { useSalesOrderIdContext } from '../../context/sales-order-id-context-provider';
import { useEntitiesSalesOrder } from '../../../hooks/entities/selectors/sales-orders';
import { useCreateReturnContext } from '../context/create-return-provider';
import { usePendingReturnContext } from '../context/pending-return-context-provider';
import { emptyObject } from 'ui-components/default-props/empty';

export const RequestInitContent = () => {
  const salesOrderId = useSalesOrderIdContext();
  const { cancelPendingEnhancedReturn } = usePendingReturnContext();
  const { isDisabled } = Init.useCreateRequest(); // isDisabled is true if no return item selection is made, else false

  const onClose = useCallback(() => cancelPendingEnhancedReturn(!isDisabled), [cancelPendingEnhancedReturn, isDisabled]);

  return <CreateEnhancedReturnDrawer onClose={onClose} salesOrderId={salesOrderId} />;
};

export const RequestDetailsContentV0 = ({ requestId }: { requestId: string }) => {
  const salesOrderId = useSalesOrderIdContext();
  const { cancelPendingEnhancedReturn } = usePendingReturnContext();

  const onClose = useCallback(() => cancelPendingEnhancedReturn(true), [cancelPendingEnhancedReturn]);

  return <CreateEnhancedReturnDrawer onClose={onClose} requestId={requestId} salesOrderId={salesOrderId} />;
};

type RequestDetailsContentProps = {
  onRequestConfirmed: (returnId: string, fulfillmentId: string) => void;
  requestId: string;
};

export const RequestDetailsContent = ({ onRequestConfirmed, requestId }: RequestDetailsContentProps) => {
  const salesOrderId = useSalesOrderIdContext();
  const salesOrder = useEntitiesSalesOrder(salesOrderId) ?? emptyObject;
  const { returnFulfillmentPlanId } = useCreateReturnContext();
  const shipFromAddress = Details.useAddress();

  const customerAddress = useMemo(() => {
    const salesOrderAddress = get(salesOrder, 'shipTos[0]', emptyObject);
    const email = get(salesOrder, 'soldTo.email', '');
    return {
      ...salesOrderAddress,
      name: shipFromAddress?.name || salesOrderAddress?.name,
      company: shipFromAddress?.company || salesOrderAddress?.company,
      line1: shipFromAddress?.line1 || salesOrderAddress?.line1,
      line2: shipFromAddress?.line2 || salesOrderAddress?.line2,
      city: shipFromAddress?.city || salesOrderAddress?.city,
      state: shipFromAddress?.state || salesOrderAddress?.state,
      postalCode: shipFromAddress?.postalCode || salesOrderAddress?.postalCode,
      countryCode: shipFromAddress?.countryCode || salesOrderAddress?.countryCode,
      phone: shipFromAddress?.phone || salesOrderAddress?.phone,
      email: shipFromAddress?.email || email,
    };
  }, [salesOrder, shipFromAddress]);

  return (
    <ReturnConfigProvider customerAddress={customerAddress} onReturnConfirmed={onRequestConfirmed} returnId={requestId}>
      <EnhancedReturnAdapterForFulfillmentPlannerContext
        fulfillmentPlanId={returnFulfillmentPlanId!}
        isAwaitingShipment
        returnId={requestId}
        salesOrderId={salesOrderId}
      >
        <FulfillmentPlanRestrictionsContextProvider fulfillmentPlanId={returnFulfillmentPlanId!} salesOrderId={salesOrderId}>
          <ConfirmReturn />
        </FulfillmentPlanRestrictionsContextProvider>
      </EnhancedReturnAdapterForFulfillmentPlannerContext>
    </ReturnConfigProvider>
  );
};
